import { Events, makeSchema, Schema, State } from '@livestore/livestore';

// ── Shared column groups (spread into tables) ─────────────────────────────────

/** Audit trail — set once on create, never overwritten except modified_timestamp. */
const auditCols = {
  created_at: State.SQLite.integer({ default: 0 }), // UTC ms
  is_edited: State.SQLite.integer({ default: 0 }),
  modified_timestamp: State.SQLite.integer({ nullable: true }), // UTC ms; updated on each edit
  original_timestamp: State.SQLite.integer({ nullable: true }), // UTC ms; immutable after create
  updated_at: State.SQLite.integer({ default: 0 }), // UTC ms
};

/**
 * Shift span — times a person is working/scheduled. Shared by timeEntries + scheduledShifts.
 *
 * FK: workplace_id → workplaces.id (enforced at commit site via assertRef)
 */
const shiftSpanCols = {
  break_minutes: State.SQLite.integer({ default: 0 }),
  ends_at: State.SQLite.integer({ nullable: true }), // UTC ms (clock-out or scheduled end)
  starts_at: State.SQLite.integer({ nullable: true }), // UTC ms (clock-in or scheduled start)
  workplace_id: State.SQLite.text({ nullable: true }),
};

// ── Shared Schema fragments ───────────────────────────────────────────────────

const AuditSchema = {
  created_at: Schema.optional(Schema.Number),
  is_edited: Schema.optional(Schema.Number),
  modified_timestamp: Schema.optional(Schema.NullOr(Schema.Number)),
  original_timestamp: Schema.optional(Schema.NullOr(Schema.Number)),
  updated_at: Schema.optional(Schema.Number),
};

const ShiftSpanSchema = {
  break_minutes: Schema.optional(Schema.Number),
  ends_at: Schema.optional(Schema.NullOr(Schema.Number)),
  starts_at: Schema.optional(Schema.NullOr(Schema.Number)),
  workplace_id: Schema.optional(Schema.NullOr(Schema.String)),
};

// ── Tables ────────────────────────────────────────────────────────────────────

export const tables = {
  // FK: workplace_id → workplaces.id, scheduled_shift_id → scheduledShifts.id
  timeEntries: State.SQLite.table({
    columns: {
      id: State.SQLite.text({ primaryKey: true }),
      ...shiftSpanCols,
      ...auditCols,
      // Per-record AuditRecord signing (features/audit-signing.md)
      audit_record_id: State.SQLite.text({ nullable: true }), // Immutable UUID, set at record creation
      // TimeEntry-specific
      client_id: State.SQLite.text({ default: '' }),
      cost_code: State.SQLite.text({ nullable: true }),
      date: State.SQLite.text({ nullable: true }), // YYYY-MM-DD logical work date
      deleted_at: State.SQLite.integer({ nullable: true }),
      entry_type: State.SQLite.text({ default: 'shift' }),
      hours_worked: State.SQLite.real({ nullable: true }),
      is_deleted: State.SQLite.integer({ default: 0 }),
      job_title: State.SQLite.text({ default: '' }),
      jws_combined: State.SQLite.text({ nullable: true }), // Combined JWS (populated at clock-out)
      jws_start: State.SQLite.text({ nullable: true }), // Start-only JWS (populated at clock-in)
      notes: State.SQLite.text({ default: '' }),
      rate_of_pay: State.SQLite.real({ default: 0 }),
      recorded_at: State.SQLite.integer({ nullable: true }), // UTC ms — device wall clock at recording time
      scheduled_shift_id: State.SQLite.text({ nullable: true }), // FK → scheduledShifts.id
      signed_at: State.SQLite.integer({ nullable: true }), // UTC ms
      unsealable_locked: State.SQLite.integer({ default: 0 }),
    },
    name: 'timeEntries',
  }),

  workplaces: State.SQLite.table({
    columns: {
      address: State.SQLite.text({ nullable: true }),
      billing_type: State.SQLite.text({ nullable: true }),
      city: State.SQLite.text({ nullable: true }),
      commission_pct: State.SQLite.real({ nullable: true }),
      contract_amount: State.SQLite.real({ nullable: true }),
      contract_description: State.SQLite.text({ nullable: true }),
      contract_period: State.SQLite.text({ nullable: true }),
      cost_codes: State.SQLite.text({ nullable: true }), // JSON array of CostCode
      created_at: State.SQLite.integer({ default: 0 }),
      default_break_minutes: State.SQLite.integer({ default: 30 }),
      default_hours: State.SQLite.real({ default: 8 }),
      deleted_at: State.SQLite.integer({ nullable: true }),
      ein: State.SQLite.text({ nullable: true }),
      flsa_classification: State.SQLite.text({ nullable: true }),
      hourly_rate: State.SQLite.real({ nullable: true }),
      id: State.SQLite.text({ primaryKey: true }),
      is_contract: State.SQLite.integer({ default: 0 }),
      is_deleted: State.SQLite.integer({ default: 0 }),
      ot_daily_hours: State.SQLite.real({ default: 0 }),
      ot_weekly_hours: State.SQLite.real({ default: 40 }),
      pay_structure: State.SQLite.text({ default: 'hourly' }),
      salary_amount: State.SQLite.real({ nullable: true }),
      salary_annual: State.SQLite.real({ nullable: true }),
      salary_period: State.SQLite.text({ nullable: true }),
      shift_differentials: State.SQLite.text({ nullable: true }), // JSON array of ShiftDifferential
      state: State.SQLite.text({ nullable: true }),
      time_rounding: State.SQLite.text({ nullable: true }),
      updated_at: State.SQLite.integer({ default: 0 }),
      workplace_name: State.SQLite.text({ default: '' }),
      zip: State.SQLite.text({ nullable: true }),
    },
    name: 'workplaces',
  }),

  // FK: workplace_id → workplaces.id (use UNASSIGNED_WORKPLACE_ID, never null)
  scheduleTemplates: State.SQLite.table({
    columns: {
      days_of_week: State.SQLite.text({ default: '[]' }),
      effective_date: State.SQLite.text({ nullable: true }),
      end_time: State.SQLite.text({ default: '17:00' }), // HH:MM template time
      id: State.SQLite.text({ primaryKey: true }),
      is_override: State.SQLite.integer({ default: 0 }),
      name: State.SQLite.text({ default: '' }),
      start_time: State.SQLite.text({ default: '09:00' }), // HH:MM template time
      unpaid_break_minutes: State.SQLite.integer({ default: 0 }),
      workplace_id: State.SQLite.text({ default: 'wp_unassigned' }),
      ...auditCols,
      deleted_at: State.SQLite.integer({ nullable: true }),
      is_deleted: State.SQLite.integer({ default: 0 }),
    },
    name: 'scheduleTemplates',
  }),

  // FK: workplace_id → workplaces.id (use UNASSIGNED_WORKPLACE_ID, never null)
  scheduledShifts: State.SQLite.table({
    columns: {
      id: State.SQLite.text({ primaryKey: true }),
      ...shiftSpanCols,
      ...auditCols,
      assigned_to: State.SQLite.text({ nullable: true }),
      assignment_version: State.SQLite.integer({ default: 0 }),
      // ScheduledShift-specific
      // workplace_id is in shiftSpanCols — overriding to remove nullable
      date: State.SQLite.text({ default: '' }), // YYYY-MM-DD; always derived from starts_at at write time
      // Team schedule sync columns
      is_locked: State.SQLite.integer({ default: 0 }),
      jws_signature: State.SQLite.text({ nullable: true }),
      manager_mutations: State.SQLite.text({ nullable: true }),
      source: State.SQLite.text({ default: '' }),
      // Plain-text workplace name from the admin's assignment — the member's device has its own
      // local workplace list, which may not have a matching record, so this is stored as display
      // text rather than resolved to a local workplace_id.
      workplace_label: State.SQLite.text({ nullable: true }),
    },
    name: 'scheduledShifts',
  }),

  signedBundles: State.SQLite.table({
    columns: {
      bundle_id: State.SQLite.text({ primaryKey: true }),
      client_id: State.SQLite.text({ default: '' }),
      created_at: State.SQLite.integer({ default: 0 }),
      payload: State.SQLite.text({ default: '{}' }),
    },
    name: 'signedBundles',
  }),

  invoices: State.SQLite.table({
    columns: {
      amount_cents: State.SQLite.integer({ default: 0 }),
      client_name: State.SQLite.text({ default: '' }),
      generated_at: State.SQLite.integer({ default: 0 }),
      id: State.SQLite.text({ primaryKey: true }),
      month_key: State.SQLite.text({ default: '' }),
      pdf_blob: State.SQLite.text({ nullable: true }),
    },
    name: 'invoices',
  }),

  billingProfiles: State.SQLite.table({
    columns: {
      address: State.SQLite.text({ nullable: true }),
      city: State.SQLite.text({ nullable: true }),
      client_email: State.SQLite.text({ default: '' }),
      client_name: State.SQLite.text({ default: '' }),
      created_at: State.SQLite.integer({ default: 0 }),
      currency: State.SQLite.text({ default: 'USD' }),
      id: State.SQLite.text({ primaryKey: true }),
      is_default: State.SQLite.integer({ default: 0 }),
      logo_image: State.SQLite.text({ nullable: true }),
      name: State.SQLite.text({ default: '' }),
      state: State.SQLite.text({ nullable: true }),
      updated_at: State.SQLite.integer({ default: 0 }),
      zip: State.SQLite.text({ nullable: true }),
    },
    name: 'billingProfiles',
  }),

  pendingUploads: State.SQLite.table({
    columns: {
      bundle_id: State.SQLite.text({ default: '' }),
      created_at: State.SQLite.integer({ default: 0 }),
      id: State.SQLite.text({ primaryKey: true }),
      kind: State.SQLite.text({ default: 'drive_bundle' }),
      payload: State.SQLite.text({ default: '{}' }),
    },
    name: 'pendingUploads',
  }),

  // FK: workplace_id → workplaces.id
  importConfigs: State.SQLite.table({
    columns: {
      created_at: State.SQLite.integer({ default: 0 }),
      id: State.SQLite.text({ primaryKey: true }),
      last_pulled_at: State.SQLite.integer({ nullable: true }),
      provider: State.SQLite.text({ default: '' }),
      sheet_id: State.SQLite.text({ default: '' }),
      updated_at: State.SQLite.integer({ default: 0 }),
      workplace_id: State.SQLite.text({ nullable: true }),
    },
    name: 'importConfigs',
  }),

  reportConfigs: State.SQLite.table({
    columns: {
      accent_color: State.SQLite.text({ nullable: true }),
      billing_profile_id: State.SQLite.text({ nullable: true }),
      created_at: State.SQLite.integer({ default: 0 }),
      id: State.SQLite.text({ primaryKey: true }),
      is_default: State.SQLite.integer({ default: 0 }),
      logo_image: State.SQLite.text({ nullable: true }),
      logo_text: State.SQLite.text({ nullable: true }),
      name: State.SQLite.text({ default: '' }),
      orientation: State.SQLite.text({ default: 'portrait' }),
      report_title: State.SQLite.text({ nullable: true }),
      report_type: State.SQLite.text({ default: 'invoice' }),
      show_billing_name: State.SQLite.integer({ default: 0 }),
      updated_at: State.SQLite.integer({ default: 0 }),
    },
    name: 'reportConfigs',
  }),

  exports: State.SQLite.table({
    columns: {
      amount_cents: State.SQLite.integer({ nullable: true }),
      client_name: State.SQLite.text({ nullable: true }),
      config_snapshot: State.SQLite.text({ nullable: true }),
      created_at: State.SQLite.integer({ default: 0 }),
      date_from: State.SQLite.text({ nullable: true }),
      date_to: State.SQLite.text({ nullable: true }),
      export_type: State.SQLite.text({ default: 'invoice' }),
      generated_at: State.SQLite.integer({ default: 0 }),
      id: State.SQLite.text({ primaryKey: true }),
      pdf_blob: State.SQLite.text({ nullable: true }),
      report_config_id: State.SQLite.text({ nullable: true }),
      updated_at: State.SQLite.integer({ default: 0 }),
      workplace_id: State.SQLite.text({ nullable: true }),
    },
    name: 'exports',
  }),

  notifications: State.SQLite.table({
    columns: {
      action_tab: State.SQLite.text({ nullable: true }),
      body: State.SQLite.text({ default: '' }),
      created_at: State.SQLite.integer({ default: 0 }),
      id: State.SQLite.text({ primaryKey: true }),
      read: State.SQLite.integer({ default: 0 }),
      title: State.SQLite.text({ default: '' }),
      type: State.SQLite.text({ default: 'info' }),
    },
    name: 'notifications',
  }),
};

// ── Events ───────────────────────────────────────────────────────────────────

const TimeEntryPayload = Schema.Struct({
  audit_record_id: Schema.optional(Schema.NullOr(Schema.String)),
  client_id: Schema.optional(Schema.String),
  cost_code: Schema.optional(Schema.NullOr(Schema.String)),
  date: Schema.optional(Schema.NullOr(Schema.String)),
  deleted_at: Schema.optional(Schema.NullOr(Schema.Number)),
  entry_type: Schema.optional(Schema.String),
  hours_worked: Schema.optional(Schema.NullOr(Schema.Number)),
  id: Schema.String,
  is_deleted: Schema.optional(Schema.Number),
  job_title: Schema.optional(Schema.String),
  jws_combined: Schema.optional(Schema.NullOr(Schema.String)),
  jws_start: Schema.optional(Schema.NullOr(Schema.String)),
  notes: Schema.optional(Schema.String),
  rate_of_pay: Schema.optional(Schema.Number),
  recorded_at: Schema.optional(Schema.NullOr(Schema.Number)),
  scheduled_shift_id: Schema.optional(Schema.NullOr(Schema.String)),
  signed_at: Schema.optional(Schema.NullOr(Schema.Number)),
  unsealable_locked: Schema.optional(Schema.Number),
  ...ShiftSpanSchema,
  ...AuditSchema,
});

const ScheduledShiftPayload = Schema.Struct({
  assigned_to: Schema.optional(Schema.NullOr(Schema.String)),
  assignment_version: Schema.optional(Schema.Number),
  date: Schema.optional(Schema.String), // derived from starts_at at write time
  id: Schema.String,
  is_locked: Schema.optional(Schema.Number),
  jws_signature: Schema.optional(Schema.NullOr(Schema.String)),
  manager_mutations: Schema.optional(Schema.NullOr(Schema.String)),
  source: Schema.optional(Schema.String),
  workplace_label: Schema.optional(Schema.NullOr(Schema.String)),
  ...ShiftSpanSchema,
  ...AuditSchema,
});

export const events = {
  auditRecordSigned: Events.synced({
    name: 'v1.AuditRecordSigned',
    schema: Schema.Struct({
      audit_record_id: Schema.String,
      id: Schema.String,
      jws_combined: Schema.optional(Schema.NullOr(Schema.String)),
      jws_start: Schema.optional(Schema.NullOr(Schema.String)),
      signed_at: Schema.Number,
    }),
  }),
  timeEntriesBulkDeleted: Events.synced({
    name: 'v1.TimeEntriesBulkDeleted',
    schema: Schema.Struct({ ids: Schema.String }), // JSON-encoded string[]
  }),
  timeEntriesBulkReassigned: Events.synced({
    name: 'v1.TimeEntriesBulkReassigned',
    schema: Schema.Struct({
      ids: Schema.String,
      updated_at: Schema.Number,
      workplace_id: Schema.String,
    }), // ids: JSON-encoded string[]
  }),
  timeEntriesBulkTypeChanged: Events.synced({
    name: 'v1.TimeEntriesBulkTypeChanged',
    schema: Schema.Struct({
      entry_type: Schema.String,
      ids: Schema.String,
      updated_at: Schema.Number,
    }), // ids: JSON-encoded string[]
  }),
  // ── TimeEntry ──────────────────────────────────────────────────────────────
  timeEntryCreated: Events.synced({
    name: 'v1.TimeEntryCreated',
    schema: TimeEntryPayload,
  }),
  timeEntryDeleted: Events.synced({
    name: 'v1.TimeEntryDeleted',
    schema: Schema.Struct({ id: Schema.String }),
  }),
  timeEntrySigned: Events.synced({
    name: 'v1.TimeEntrySigned',
    schema: Schema.Struct({ id: Schema.String, signed_at: Schema.Number }),
  }),
  timeEntryUnsealed: Events.synced({
    name: 'v1.TimeEntryUnsealed',
    schema: Schema.Struct({
      break_minutes: Schema.Number,
      // Clone fields (all editable fields, no sealed/signing fields)
      client_id: Schema.String,
      cloneId: Schema.String, // new clone entry id
      cost_code: Schema.optional(Schema.NullOr(Schema.String)),
      created_at: Schema.Number,
      date: Schema.optional(Schema.NullOr(Schema.String)),
      deletedAt: Schema.Number, // UTC ms
      ends_at: Schema.optional(Schema.NullOr(Schema.Number)),
      entry_type: Schema.optional(Schema.String),
      hours_worked: Schema.optional(Schema.NullOr(Schema.Number)),
      id: Schema.String, // original entry id (soft-deleted)
      job_title: Schema.optional(Schema.String),
      notes: Schema.optional(Schema.String),
      original_timestamp: Schema.Number,
      rate_of_pay: Schema.Number,
      scheduled_shift_id: Schema.optional(Schema.NullOr(Schema.String)),
      starts_at: Schema.optional(Schema.NullOr(Schema.Number)),
      updated_at: Schema.Number,
      workplace_id: Schema.optional(Schema.NullOr(Schema.String)),
    }),
  }),
  timeEntryUpdated: Events.synced({
    name: 'v1.TimeEntryUpdated',
    // original_timestamp intentionally excluded — immutable after create
    schema: Schema.Struct({
      cost_code: Schema.optional(Schema.NullOr(Schema.String)),
      date: Schema.optional(Schema.NullOr(Schema.String)),
      entry_type: Schema.optional(Schema.String),
      hours_worked: Schema.optional(Schema.NullOr(Schema.Number)),
      id: Schema.String,
      job_title: Schema.optional(Schema.String),
      notes: Schema.optional(Schema.String),
      ...ShiftSpanSchema,
      is_edited: Schema.Number,
      modified_timestamp: Schema.Number,
      updated_at: Schema.Number,
    }),
  }),

  // ── Workplace ─────────────────────────────────────────────────────────────
  workplaceCreated: Events.synced({
    name: 'v1.WorkplaceCreated',
    schema: Schema.Struct({
      address: Schema.optional(Schema.NullOr(Schema.String)),
      billing_type: Schema.optional(Schema.NullOr(Schema.String)),
      city: Schema.optional(Schema.NullOr(Schema.String)),
      commission_pct: Schema.optional(Schema.NullOr(Schema.Number)),
      contract_amount: Schema.optional(Schema.NullOr(Schema.Number)),
      contract_description: Schema.optional(Schema.NullOr(Schema.String)),
      contract_period: Schema.optional(Schema.NullOr(Schema.String)),
      cost_codes: Schema.optional(Schema.NullOr(Schema.String)), // JSON array
      created_at: Schema.Number,
      default_break_minutes: Schema.optional(Schema.Number),
      default_hours: Schema.optional(Schema.Number),
      ein: Schema.optional(Schema.NullOr(Schema.String)),
      flsa_classification: Schema.optional(Schema.NullOr(Schema.String)),
      hourly_rate: Schema.optional(Schema.NullOr(Schema.Number)),
      id: Schema.String,
      is_contract: Schema.optional(Schema.Number),
      ot_daily_hours: Schema.optional(Schema.Number),
      ot_weekly_hours: Schema.optional(Schema.Number),
      pay_structure: Schema.optional(Schema.String),
      salary_amount: Schema.optional(Schema.NullOr(Schema.Number)),
      salary_annual: Schema.optional(Schema.NullOr(Schema.Number)),
      salary_period: Schema.optional(Schema.NullOr(Schema.String)),
      shift_differentials: Schema.optional(Schema.NullOr(Schema.String)), // JSON array
      state: Schema.optional(Schema.NullOr(Schema.String)),
      time_rounding: Schema.optional(Schema.NullOr(Schema.String)),
      updated_at: Schema.Number,
      workplace_name: Schema.String,
      zip: Schema.optional(Schema.NullOr(Schema.String)),
    }),
  }),
  // Soft delete only -- the row is flagged, not removed, so a deletion itself can propagate
  // through Drive/OneDrive sync's last-write-wins merge instead of a vanished row looking like
  // "never existed" and getting silently re-inserted from a stale cloud backup. Purged for real
  // by workplacePurged after a retention window (see purgeExpiredWorkplaces.ts).
  workplaceDeleted: Events.synced({
    name: 'v1.WorkplaceDeleted',
    schema: Schema.Struct({ deleted_at: Schema.Number, id: Schema.String }),
  }),
  workplacePurged: Events.synced({
    name: 'v1.WorkplacePurged',
    schema: Schema.Struct({ id: Schema.String }),
  }),
  workplaceUpdated: Events.synced({
    name: 'v1.WorkplaceUpdated',
    schema: Schema.Struct({
      address: Schema.optional(Schema.NullOr(Schema.String)),
      billing_type: Schema.optional(Schema.NullOr(Schema.String)),
      city: Schema.optional(Schema.NullOr(Schema.String)),
      commission_pct: Schema.optional(Schema.NullOr(Schema.Number)),
      contract_amount: Schema.optional(Schema.NullOr(Schema.Number)),
      contract_description: Schema.optional(Schema.NullOr(Schema.String)),
      contract_period: Schema.optional(Schema.NullOr(Schema.String)),
      cost_codes: Schema.optional(Schema.NullOr(Schema.String)), // JSON array
      default_break_minutes: Schema.optional(Schema.Number),
      default_hours: Schema.optional(Schema.Number),
      ein: Schema.optional(Schema.NullOr(Schema.String)),
      flsa_classification: Schema.optional(Schema.NullOr(Schema.String)),
      hourly_rate: Schema.optional(Schema.NullOr(Schema.Number)),
      id: Schema.String,
      is_contract: Schema.optional(Schema.Number),
      ot_daily_hours: Schema.optional(Schema.Number),
      ot_weekly_hours: Schema.optional(Schema.Number),
      pay_structure: Schema.optional(Schema.String),
      salary_amount: Schema.optional(Schema.NullOr(Schema.Number)),
      salary_annual: Schema.optional(Schema.NullOr(Schema.Number)),
      salary_period: Schema.optional(Schema.NullOr(Schema.String)),
      shift_differentials: Schema.optional(Schema.NullOr(Schema.String)), // JSON array
      state: Schema.optional(Schema.NullOr(Schema.String)),
      time_rounding: Schema.optional(Schema.NullOr(Schema.String)),
      updated_at: Schema.Number,
      workplace_name: Schema.optional(Schema.String),
      zip: Schema.optional(Schema.NullOr(Schema.String)),
    }),
  }),

  // ── Schedule (template — HH:MM times, no date) ───────────────────────────
  scheduleTemplateCreated: Events.synced({
    name: 'v1.ScheduleTemplateCreated',
    schema: Schema.Struct({
      days_of_week: Schema.String,
      effective_date: Schema.optional(Schema.NullOr(Schema.String)),
      end_time: Schema.String,
      id: Schema.String,
      is_override: Schema.optional(Schema.Number),
      name: Schema.String,
      start_time: Schema.String,
      unpaid_break_minutes: Schema.Number,
      workplace_id: Schema.String, // use UNASSIGNED_WORKPLACE_ID, never null
      ...AuditSchema,
    }),
  }),
  scheduleTemplateDeleted: Events.synced({
    name: 'v1.ScheduleTemplateDeleted',
    schema: Schema.Struct({ deleted_at: Schema.Number, id: Schema.String }),
  }),
  scheduleTemplatePurged: Events.synced({
    name: 'v1.ScheduleTemplatePurged',
    schema: Schema.Struct({ id: Schema.String }),
  }),
  scheduleTemplateUpdated: Events.synced({
    name: 'v1.ScheduleTemplateUpdated',
    schema: Schema.Struct({
      days_of_week: Schema.optional(Schema.String),
      effective_date: Schema.optional(Schema.NullOr(Schema.String)),
      end_time: Schema.optional(Schema.String),
      id: Schema.String,
      is_edited: Schema.Number,
      is_override: Schema.optional(Schema.Number),
      modified_timestamp: Schema.Number,
      name: Schema.optional(Schema.String),
      start_time: Schema.optional(Schema.String),
      unpaid_break_minutes: Schema.optional(Schema.Number),
      updated_at: Schema.Number,
      workplace_id: Schema.optional(Schema.String),
    }),
  }),

  // ── ScheduledShift ────────────────────────────────────────────────────────
  scheduledShiftCreated: Events.synced({
    name: 'v1.ScheduledShiftCreated',
    schema: ScheduledShiftPayload,
  }),
  scheduledShiftDeleted: Events.synced({
    name: 'v1.ScheduledShiftDeleted',
    schema: Schema.Struct({ id: Schema.String }),
  }),
  scheduledShiftUpdated: Events.synced({
    name: 'v1.ScheduledShiftUpdated',
    schema: Schema.Struct({
      assigned_to: Schema.optional(Schema.NullOr(Schema.String)),
      assignment_version: Schema.optional(Schema.Number),
      id: Schema.String,
      is_locked: Schema.optional(Schema.Number),
      jws_signature: Schema.optional(Schema.NullOr(Schema.String)),
      manager_mutations: Schema.optional(Schema.NullOr(Schema.String)),
      source: Schema.optional(Schema.String),
      workplace_label: Schema.optional(Schema.NullOr(Schema.String)),
      ...ShiftSpanSchema,
      modified_timestamp: Schema.optional(Schema.Number),
      updated_at: Schema.Number,
    }),
  }),

  // ── SignedBundle ──────────────────────────────────────────────────────────
  signedBundleStored: Events.synced({
    name: 'v1.SignedBundleStored',
    schema: Schema.Struct({
      bundle_id: Schema.String,
      client_id: Schema.String,
      created_at: Schema.Number,
      payload: Schema.String,
    }),
  }),

  // ── Invoice ───────────────────────────────────────────────────────────────
  invoiceCreated: Events.synced({
    name: 'v1.InvoiceCreated',
    schema: Schema.Struct({
      amount_cents: Schema.Number,
      client_name: Schema.String,
      generated_at: Schema.Number,
      id: Schema.String,
      month_key: Schema.String,
      pdf_blob: Schema.optional(Schema.NullOr(Schema.String)),
    }),
  }),

  // ── BillingProfile ────────────────────────────────────────────────────────
  billingProfileCreated: Events.synced({
    name: 'v1.BillingProfileCreated',
    schema: Schema.Struct({
      address: Schema.optional(Schema.NullOr(Schema.String)),
      city: Schema.optional(Schema.NullOr(Schema.String)),
      client_email: Schema.optional(Schema.String),
      client_name: Schema.optional(Schema.String),
      created_at: Schema.Number,
      currency: Schema.optional(Schema.String),
      id: Schema.String,
      is_default: Schema.optional(Schema.Number),
      logo_image: Schema.optional(Schema.NullOr(Schema.String)),
      name: Schema.String,
      state: Schema.optional(Schema.NullOr(Schema.String)),
      updated_at: Schema.Number,
      zip: Schema.optional(Schema.NullOr(Schema.String)),
    }),
  }),
  billingProfileDeleted: Events.synced({
    name: 'v1.BillingProfileDeleted',
    schema: Schema.Struct({ id: Schema.String }),
  }),
  billingProfileUpdated: Events.synced({
    name: 'v1.BillingProfileUpdated',
    schema: Schema.Struct({
      address: Schema.optional(Schema.NullOr(Schema.String)),
      city: Schema.optional(Schema.NullOr(Schema.String)),
      client_email: Schema.optional(Schema.String),
      client_name: Schema.optional(Schema.String),
      currency: Schema.optional(Schema.String),
      id: Schema.String,
      is_default: Schema.optional(Schema.Number),
      logo_image: Schema.optional(Schema.NullOr(Schema.String)),
      name: Schema.optional(Schema.String),
      state: Schema.optional(Schema.NullOr(Schema.String)),
      updated_at: Schema.Number,
      zip: Schema.optional(Schema.NullOr(Schema.String)),
    }),
  }),

  pendingUploadCleared: Events.synced({
    name: 'v1.PendingUploadCleared',
    schema: Schema.Struct({ id: Schema.String }),
  }),
  // ── PendingUpload ─────────────────────────────────────────────────────────
  pendingUploadQueued: Events.synced({
    name: 'v1.PendingUploadQueued',
    schema: Schema.Struct({
      bundle_id: Schema.String,
      created_at: Schema.Number,
      id: Schema.String,
      kind: Schema.optional(Schema.String),
      payload: Schema.String,
    }),
  }),

  importConfigDeleted: Events.synced({
    name: 'v1.ImportConfigDeleted',
    schema: Schema.Struct({ id: Schema.String }),
  }),
  importConfigPullTimestamped: Events.synced({
    name: 'v1.ImportConfigPullTimestamped',
    schema: Schema.Struct({ id: Schema.String, last_pulled_at: Schema.Number }),
  }),
  // ── ImportConfig ──────────────────────────────────────────────────────────
  importConfigSaved: Events.synced({
    name: 'v1.ImportConfigSaved',
    schema: Schema.Struct({
      created_at: Schema.Number,
      id: Schema.String,
      last_pulled_at: Schema.NullOr(Schema.Number),
      provider: Schema.String,
      sheet_id: Schema.String,
      updated_at: Schema.Number,
      workplace_id: Schema.NullOr(Schema.String),
    }),
  }),

  reportConfigDeleted: Events.synced({
    name: 'v1.ReportConfigDeleted',
    schema: Schema.Struct({ id: Schema.String }),
  }),
  // ── ReportConfig ──────────────────────────────────────────────────────────
  reportConfigSaved: Events.synced({
    name: 'v1.ReportConfigSaved',
    schema: Schema.Struct({
      accent_color: Schema.optional(Schema.NullOr(Schema.String)),
      billing_profile_id: Schema.optional(Schema.NullOr(Schema.String)),
      created_at: Schema.Number,
      id: Schema.String,
      is_default: Schema.optional(Schema.Number),
      logo_image: Schema.optional(Schema.NullOr(Schema.String)),
      logo_text: Schema.optional(Schema.NullOr(Schema.String)),
      name: Schema.String,
      orientation: Schema.optional(Schema.String),
      report_title: Schema.optional(Schema.NullOr(Schema.String)),
      report_type: Schema.optional(Schema.String),
      show_billing_name: Schema.optional(Schema.Number),
      updated_at: Schema.Number,
    }),
  }),

  // ── Export ────────────────────────────────────────────────────────────────
  exportCreated: Events.synced({
    name: 'v1.ExportCreated',
    schema: Schema.Struct({
      amount_cents: Schema.optional(Schema.NullOr(Schema.Number)),
      client_name: Schema.optional(Schema.NullOr(Schema.String)),
      config_snapshot: Schema.optional(Schema.NullOr(Schema.String)),
      created_at: Schema.Number,
      date_from: Schema.optional(Schema.NullOr(Schema.String)),
      date_to: Schema.optional(Schema.NullOr(Schema.String)),
      export_type: Schema.String,
      generated_at: Schema.Number,
      id: Schema.String,
      pdf_blob: Schema.optional(Schema.NullOr(Schema.String)),
      report_config_id: Schema.optional(Schema.NullOr(Schema.String)),
      updated_at: Schema.Number,
      workplace_id: Schema.optional(Schema.NullOr(Schema.String)),
    }),
  }),
  exportDeleted: Events.synced({
    name: 'v1.ExportDeleted',
    schema: Schema.Struct({ id: Schema.String }),
  }),

  // ── Notification ───────────────────────────────────────────────────────────
  notificationCreated: Events.synced({
    name: 'v1.NotificationCreated',
    schema: Schema.Struct({
      action_tab: Schema.optional(Schema.NullOr(Schema.String)),
      body: Schema.String,
      created_at: Schema.Number,
      id: Schema.String,
      read: Schema.optional(Schema.Number),
      title: Schema.String,
      type: Schema.optional(Schema.String),
    }),
  }),
  notificationDismissed: Events.synced({
    name: 'v1.NotificationDismissed',
    schema: Schema.Struct({ id: Schema.String }),
  }),
  notificationRead: Events.synced({
    name: 'v1.NotificationRead',
    schema: Schema.Struct({ id: Schema.String }),
  }),
};

// ── Materializers ─────────────────────────────────────────────────────────────

// Editable fields carried over from the original entry into an unsealed clone, with their
// fallback when the event payload didn't set one -- kept as a lookup table, not inline `??`
// expressions, purely to keep buildUnsealedClonePayload's complexity under the project's cap
// (ESLint's complexity rule counts each `??` as a branch).
// Exported so callers building a v1.TimeEntryUnsealed payload (e.g. handleUnsealEntry) use the
// same fallbacks -- client_id/rate_of_pay/break_minutes are non-nullable in the event schema
// below, so a caller that falls back to `null` instead of these defaults gets a synchronous,
// easy-to-miss schema construction error instead of a committed event.
export const UNSEALED_CLONE_FIELD_DEFAULTS: Record<string, unknown> = {
  break_minutes: 0,
  client_id: '',
  cost_code: null,
  date: null,
  ends_at: null,
  entry_type: 'shift',
  hours_worked: null,
  job_title: '',
  notes: '',
  rate_of_pay: 0,
  scheduled_shift_id: null,
  starts_at: null,
  workplace_id: null,
};

function withDefaults(
  p: Record<string, unknown>,
  defaults: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, fallback] of Object.entries(defaults)) out[key] = p[key] ?? fallback;
  return out;
}

// Split out of the 'v1.TimeEntryUnsealed' materializer purely to keep that function's own
// complexity under the project's cap. Every sealed/signing field is explicitly nulled and
// unsealable_locked forced to 1 so this clone can never be sealed again.
function buildUnsealedClonePayload(
  p: Record<string, unknown> & {
    cloneId: string;
    created_at: number;
    original_timestamp: number;
    updated_at: number;
  },
) {
  return {
    id: p.cloneId,
    ...withDefaults(p, UNSEALED_CLONE_FIELD_DEFAULTS),
    audit_record_id: null,
    created_at: p.created_at,
    deleted_at: null,
    is_deleted: 0,
    is_edited: 0,
    jws_combined: null,
    jws_start: null,
    modified_timestamp: p.original_timestamp,
    original_timestamp: p.original_timestamp,
    recorded_at: null,
    signed_at: null,
    unsealable_locked: 1,
    updated_at: p.updated_at,
  };
}

// Exported raw (event name -> query-builder fn) map, independent of State.SQLite.materializers'
// wrapped return shape, so the real SQLite storage adapter (src/db/sqliteStore.ts) can run the
// same event->query mapping itself via each query object's .asSql() method, without needing to
// reverse-engineer @livestore/livestore's internal materializer runner.
export const rawMaterializers = {
  'v1.AuditRecordSigned': ({ audit_record_id, id, jws_combined, jws_start, signed_at }) =>
    tables.timeEntries
      .update({ audit_record_id, jws_combined, jws_start, signed_at })
      .where({ id }),
  'v1.TimeEntriesBulkDeleted': ({ ids }) =>
    (JSON.parse(ids) as string[]).map(id => tables.timeEntries.delete().where({ id })),
  'v1.TimeEntriesBulkReassigned': ({ ids, updated_at, workplace_id }) =>
    (JSON.parse(ids) as string[]).map(id =>
      tables.timeEntries.update({ updated_at, workplace_id }).where({ id }),
    ),
  'v1.TimeEntriesBulkTypeChanged': ({ entry_type, ids, updated_at }) =>
    (JSON.parse(ids) as string[]).map(id =>
      tables.timeEntries.update({ entry_type, updated_at }).where({ id }),
    ),
  'v1.TimeEntryCreated': p => tables.timeEntries.insert(p),
  'v1.TimeEntryDeleted': ({ id }) => tables.timeEntries.delete().where({ id }),
  'v1.TimeEntrySigned': ({ id, signed_at }) =>
    tables.timeEntries.update({ signed_at }).where({ id }),
  'v1.TimeEntryUnsealed': p => [
    tables.timeEntries
      .update({
        deleted_at: p.deletedAt,
        is_deleted: 1,
      })
      .where({ id: p.id }),
    tables.timeEntries.insert(buildUnsealedClonePayload(p)),
  ],
  'v1.TimeEntryUpdated': ({ id, ...fields }) => tables.timeEntries.update(fields).where({ id }),

  // is_deleted defaults to 0 here (not just in the SQL column DEFAULT) because the in-memory
  // reactive mirror (inMemoryTableStore.ts) doesn't apply column defaults itself -- an omitted
  // is_deleted would read as undefined until the next real-SQLite rehydration, making a
  // just-created workplace invisible to any .where({ is_deleted: 0 }) list query in the same
  // session.
  'v1.WorkplaceCreated': p => tables.workplaces.insert({ is_deleted: 0, ...p }),
  'v1.WorkplaceDeleted': ({ deleted_at, id }) =>
    tables.workplaces.update({ deleted_at, is_deleted: 1 }).where({ id }),
  'v1.WorkplacePurged': ({ id }) => tables.workplaces.delete().where({ id }),
  'v1.WorkplaceUpdated': ({ id, ...fields }) => tables.workplaces.update(fields).where({ id }),

  'v1.ScheduleTemplateCreated': p => tables.scheduleTemplates.insert({ is_deleted: 0, ...p }),
  'v1.ScheduleTemplateDeleted': ({ deleted_at, id }) =>
    tables.scheduleTemplates.update({ deleted_at, is_deleted: 1 }).where({ id }),
  'v1.ScheduleTemplatePurged': ({ id }) => tables.scheduleTemplates.delete().where({ id }),
  'v1.ScheduleTemplateUpdated': ({ id, ...fields }) =>
    tables.scheduleTemplates.update(fields).where({ id }),

  'v1.ScheduledShiftCreated': p => tables.scheduledShifts.insert(p),
  'v1.ScheduledShiftDeleted': ({ id }) => tables.scheduledShifts.delete().where({ id }),
  'v1.ScheduledShiftUpdated': ({ id, ...fields }) =>
    tables.scheduledShifts.update(fields).where({ id }),

  'v1.SignedBundleStored': p => tables.signedBundles.insert(p),

  'v1.InvoiceCreated': p => tables.invoices.insert(p),

  'v1.BillingProfileCreated': p => tables.billingProfiles.insert(p),
  'v1.BillingProfileDeleted': ({ id }) => tables.billingProfiles.delete().where({ id }),
  'v1.BillingProfileUpdated': ({ id, ...fields }) =>
    tables.billingProfiles.update(fields).where({ id }),

  'v1.PendingUploadCleared': ({ id }) => tables.pendingUploads.delete().where({ id }),
  'v1.PendingUploadQueued': p => tables.pendingUploads.insert(p),

  'v1.ImportConfigDeleted': ({ id }) => tables.importConfigs.delete().where({ id }),
  'v1.ImportConfigPullTimestamped': ({ id, last_pulled_at }) =>
    tables.importConfigs.update({ last_pulled_at }).where({ id }),
  'v1.ImportConfigSaved': p => [
    tables.importConfigs.delete().where({ id: p.id }),
    tables.importConfigs.insert(p),
  ],

  'v1.ExportCreated': p => tables.exports.insert(p),
  'v1.ExportDeleted': ({ id }) => tables.exports.delete().where({ id }),
  'v1.ReportConfigDeleted': ({ id }) => tables.reportConfigs.delete().where({ id }),
  'v1.ReportConfigSaved': p => tables.reportConfigs.insert(p),

  'v1.NotificationCreated': p => tables.notifications.insert(p),
  'v1.NotificationDismissed': ({ id }) => tables.notifications.delete().where({ id }),
  'v1.NotificationRead': ({ id }) => tables.notifications.update({ read: 1 }).where({ id }),
};

const materializers = State.SQLite.materializers(events, rawMaterializers);

const state = State.SQLite.makeState({ materializers, tables });

export const schema = makeSchema({ events, state });
